import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Copy, Check, BookOpen, Lock, Globe, Zap, MessageSquare, Trophy, BookMarked, Brain, ShieldCheck, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const BASE_URL = "/api/mobile/v1";

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };
  return { copied, copy };
}

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: "bg-emerald-500 text-white",
    POST: "bg-blue-500 text-white",
    PUT: "bg-violet-500 text-white",
    PATCH: "bg-amber-500 text-white",
    DELETE: "bg-red-500 text-white",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold tracking-wide ${colors[method] ?? "bg-muted text-muted-foreground"}`}>
      {method}
    </span>
  );
}

function AuthBadge({ required }: { required: boolean }) {
  return required ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
      <Lock className="w-3 h-3" /> Auth
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
      <Globe className="w-3 h-3" /> Public
    </span>
  );
}

function CodeBlock({ code, id }: { code: string; id: string }) {
  const { copied, copy } = useCopy();
  return (
    <div className="relative group">
      <pre className="bg-slate-900 text-slate-100 text-xs rounded-lg p-4 overflow-x-auto leading-relaxed">
        <code>{code}</code>
      </pre>
      <Button
        size="icon"
        variant="ghost"
        className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-white hover:bg-slate-700"
        onClick={() => copy(code, id)}
      >
        {copied === id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </Button>
    </div>
  );
}

interface Endpoint {
  method: string;
  path: string;
  auth: boolean;
  description: string;
  params?: Record<string, string>;
  body?: Record<string, string>;
  response?: string;
  note?: string;
}

function EndpointCard({ ep }: { ep: Endpoint }) {
  const [open, setOpen] = useState(false);
  const fullPath = `${BASE_URL}${ep.path}`;

  const exampleCurl = `curl -X ${ep.method} https://edufocalacademy.com${fullPath}${
    ep.auth ? `\n  -H "Authorization: Bearer <your_access_token>"` : ""
  }${
    ep.body
      ? `\n  -H "Content-Type: application/json"\n  -d '${JSON.stringify(
          Object.fromEntries(Object.entries(ep.body).map(([k]) => [k, `<${k}>`])),
          null, 2
        )}'`
      : ""
  }`;

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <MethodBadge method={ep.method} />
        <code className="text-sm font-mono text-foreground flex-1">{fullPath}</code>
        <AuthBadge required={ep.auth} />
        <span className={`text-sm transition-transform ${open ? "rotate-180" : ""} text-muted-foreground`}>▾</span>
      </button>

      {open && (
        <div className="border-t px-4 py-4 space-y-4 bg-muted/20">
          <p className="text-sm text-muted-foreground">{ep.description}</p>

          {ep.note && (
            <div className="text-xs rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 px-3 py-2 text-blue-700 dark:text-blue-300">
              {ep.note}
            </div>
          )}

          {ep.params && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Query Parameters</p>
              <div className="space-y-1">
                {Object.entries(ep.params).map(([name, desc]) => (
                  <div key={name} className="flex gap-3 text-xs">
                    <code className="text-violet-600 dark:text-violet-400 min-w-[140px]">{name}</code>
                    <span className="text-muted-foreground">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ep.body && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Request Body</p>
              <div className="space-y-1">
                {Object.entries(ep.body).map(([name, desc]) => (
                  <div key={name} className="flex gap-3 text-xs">
                    <code className="text-violet-600 dark:text-violet-400 min-w-[140px]">{name}</code>
                    <span className="text-muted-foreground">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ep.response && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Example Response</p>
              <CodeBlock code={ep.response} id={`resp-${ep.method}-${ep.path}`} />
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Example Request</p>
            <CodeBlock code={exampleCurl} id={`curl-${ep.method}-${ep.path}`} />
          </div>
        </div>
      )}
    </div>
  );
}

interface Section {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  endpoints: Endpoint[];
}

const sections: Section[] = [
  {
    id: "auth",
    icon: ShieldCheck,
    title: "Authentication",
    description: "Create accounts, log in, and manage access tokens. Access tokens do not expire. Use /auth/refresh only to rotate your refresh token.",
    endpoints: [
      {
        method: "POST", path: "/auth/signup", auth: false,
        description: "Create a new student account.",
        body: {
          email: "string (required)",
          password: "string (required, min 8 chars)",
          firstName: "string (required)",
          lastName: "string (required)",
          grade: "number (required): 4, 5, or 6",
        },
        response: `{\n  "accessToken": "eyJ...",\n  "refreshToken": "eyJ...",\n  "user": { "id": "uuid", "email": "...", "firstName": "..." }\n}`,
      },
      {
        method: "POST", path: "/auth/login", auth: false,
        description: "Authenticate with email and password.",
        body: { email: "string (required)", password: "string (required)" },
        response: `{\n  "accessToken": "eyJ...",\n  "refreshToken": "eyJ...",\n  "user": { "id": "uuid", "email": "...", "grade": 5 }\n}`,
      },
      {
        method: "POST", path: "/auth/refresh", auth: false,
        description: "Exchange a valid refresh token for a new access token.",
        body: { refreshToken: "string (required)" },
        response: `{ "accessToken": "eyJ...", "refreshToken": "eyJ..." }`,
        note: "Access tokens do not expire. Refresh tokens last 30 days.",
      },
      {
        method: "POST", path: "/auth/logout", auth: true,
        description: "Revoke the current refresh token.",
        body: { refreshToken: "string (required)" },
        response: `{ "success": true }`,
      },
      {
        method: "POST", path: "/auth/logout-all", auth: true,
        description: "Revoke all refresh tokens, which logs out from every device.",
        response: `{ "success": true }`,
      },
    ],
  },
  {
    id: "user",
    icon: BookOpen,
    title: "User & Profile",
    description: "Retrieve and update the authenticated student's profile, XP level, streaks, progress, and achievements.",
    endpoints: [
      {
        method: "GET", path: "/user/profile", auth: true,
        description: "Get the current user's profile information.",
        response: `{\n  "id": "uuid",\n  "email": "...",\n  "firstName": "...",\n  "lastName": "...",\n  "grade": 5,\n  "username": "...",\n  "profilePhotoUrl": "https://..."\n}`,
      },
      {
        method: "PUT", path: "/user/profile", auth: true,
        description: "Update profile fields.",
        body: {
          firstName: "string (optional)",
          lastName: "string (optional)",
          username: "string (optional)",
          school: "string (optional)",
          gender: "string (optional)",
          dateOfBirth: "string (optional, YYYY-MM-DD)",
        },
        response: `{ "success": true, "user": { ... } }`,
      },
      {
        method: "GET", path: "/user/level", auth: true,
        description: "Get the user's current XP level across all subjects.",
        response: `{\n  "level": 7,\n  "mathXp": 1240,\n  "englishXp": 890,\n  "scienceXp": 540,\n  "socialStudiesXp": 200\n}`,
      },
      {
        method: "GET", path: "/user/stats", auth: true,
        description: "Get performance statistics and subject breakdown.",
        response: `{\n  "totalPoints": 2870,\n  "accuracy": 78.4,\n  "totalTests": 42,\n  "subjectBreakdown": [{ "subject": "math", "accuracy": 82, "totalAnswered": 120 }]\n}`,
      },
      {
        method: "GET", path: "/user/progress", auth: true,
        description: "Get detailed learning progress per topic.",
        response: `[{ "topicId": "...", "topic": "Fractions", "totalAnswered": 30, "correct": 24 }]`,
      },
      {
        method: "GET", path: "/user/achievements", auth: true,
        description: "Get the user's earned achievement badges.",
        response: `[{ "id": "...", "name": "First 10 Correct", "earnedAt": "2025-01-10T..." }]`,
      },
      {
        method: "GET", path: "/user/streak", auth: true,
        description: "Get the user's current and longest daily streak.",
        response: `{ "currentStreak": 5, "longestStreak": 12, "lastActiveDate": "2025-01-14" }`,
      },
      {
        method: "PUT", path: "/user/password", auth: true,
        description: "Change the account password.",
        body: { currentPassword: "string (required)", newPassword: "string (required, min 8 chars)" },
        response: `{ "success": true }`,
      },
      {
        method: "GET", path: "/user/profile/upload-url", auth: true,
        description: "Get a presigned URL to upload a profile photo to object storage.",
        response: `{ "uploadURL": "https://storage.googleapis.com/...", "publicURL": "https://..." }`,
      },
      {
        method: "PUT", path: "/user/profile/photo", auth: true,
        description: "Save the profile photo URL after uploading to storage.",
        body: { photoUrl: "string (required): public URL from upload step" },
        response: `{ "success": true }`,
      },
    ],
  },
  {
    id: "content",
    icon: BookMarked,
    title: "Content & Questions",
    description: "Browse curriculum grades, subjects, topics, and fetch quiz questions.",
    endpoints: [
      {
        method: "GET", path: "/grades", auth: false,
        description: "List all available curriculum grades.",
        response: `[{ "id": "...", "gradeNumber": 4, "name": "Grade 4" }, ...]`,
      },
      {
        method: "GET", path: "/grades/:gradeNumber/subjects", auth: true,
        description: "Get subjects available for a specific grade (e.g. grade 5).",
        response: `[{ "id": "...", "name": "math", "displayName": "Mathematics", "totalTopics": 12 }]`,
      },
      {
        method: "GET", path: "/subjects/:subjectId/topics", auth: true,
        description: "Get all curriculum topics within a subject, scoped to the user's grade.",
        response: `[{ "id": "...", "name": "Fractions", "questionCount": 45 }]`,
      },
      {
        method: "GET", path: "/questions", auth: true,
        description: "Fetch quiz questions for a specific topic.",
        params: {
          subject: "string (required): e.g. math",
          topic: "string (required): topic name",
          difficulty: "string (optional): easy | medium | hard",
          limit: "number (optional, default 10)",
          random: "boolean (optional): shuffle questions",
          seed: "number (optional): reproducible shuffle seed",
        },
        response: `[{ "id": "...", "questionText": "...", "options": [...], "difficulty": "medium", "points": 10 }]`,
        note: "Options are returned without the correct answer. Submit the user's answer to /questions/:id/submit.",
      },
      {
        method: "POST", path: "/questions/:questionId/submit", auth: true,
        description: "Submit an answer for a question. XP is awarded for correct answers.",
        body: { selectedAnswer: "string (required): the selected option ID" },
        response: `{\n  "correct": true,\n  "correctAnswer": "option-id-abc",\n  "explanation": "Because...",\n  "pointsEarned": 10\n}`,
      },
    ],
  },
  {
    id: "reading",
    icon: BookOpen,
    title: "Reading Library",
    description: "Access structured reading materials and track reading progress.",
    endpoints: [
      {
        method: "GET", path: "/reading", auth: true,
        description: "Get reading materials for the user's grade.",
        response: `[{ "id": "...", "title": "...", "subject": "english", "slug": "...", "isCompleted": false }]`,
      },
      {
        method: "GET", path: "/reading/:slug", auth: true,
        description: "Get a specific reading material including its HTML content.",
        response: `{ "id": "...", "title": "...", "htmlContent": "<p>...</p>", "wordCount": 450 }`,
      },
      {
        method: "POST", path: "/reading/:materialId/start", auth: true,
        description: "Mark the start of reading a material.",
        response: `{ "success": true }`,
      },
      {
        method: "POST", path: "/reading/:materialId/progress", auth: true,
        description: "Update reading scroll position.",
        body: { scrollPercentage: "number (required): 0 to 100" },
        response: `{ "success": true }`,
      },
      {
        method: "POST", path: "/reading/:materialId/complete", auth: true,
        description: "Mark a reading material as fully completed.",
        response: `{ "success": true, "xpEarned": 25 }`,
      },
    ],
  },
  {
    id: "testing",
    icon: Zap,
    title: "Testing",
    description: "Submit batched test results with built-in anti-grinding and XP calculation.",
    endpoints: [
      {
        method: "POST", path: "/test/results", auth: true,
        description: "Submit a batch of test answers at once (e.g. at the end of a quiz session).",
        body: {
          subject: "string (required)",
          topic: "string (required)",
          "results[]": "array of { questionId, selectedAnswer, timeTakenMs }",
        },
        response: `{\n  "totalQuestions": 10,\n  "correct": 7,\n  "accuracy": 70,\n  "xpEarned": 85,\n  "levelUp": false\n}`,
        note: "Anti-grinding protection limits XP gain per topic per day.",
      },
    ],
  },
  {
    id: "leaderboard",
    icon: Trophy,
    title: "Leaderboard & Challenges",
    description: "Access the live leaderboard and student challenges.",
    endpoints: [
      {
        method: "GET", path: "/leaderboard", auth: true,
        description: "Get the top 20 students leaderboard, scoped to the user's grade.",
        response: `[{ "rank": 1, "user": { "firstName": "..." }, "totalPoints": 3200, "effectivePoints": 2900 }]`,
        note: "effectivePoints applies the XP decay factor for inactive users.",
      },
      {
        method: "GET", path: "/challenges", auth: true,
        description: "Get available and pending challenges.",
        response: `[{ "id": "...", "challenger": {...}, "status": "pending", "expiresAt": "..." }]`,
      },
      {
        method: "GET", path: "/challenges/:id", auth: true,
        description: "Get full details of a specific challenge.",
        response: `{ "id": "...", "challenger": {...}, "questions": [...], "status": "active" }`,
      },
      {
        method: "POST", path: "/challenges/:id/accept", auth: true,
        description: "Accept an incoming challenge.",
        response: `{ "success": true, "challenge": { ... } }`,
      },
      {
        method: "POST", path: "/challenges/:id/decline", auth: true,
        description: "Decline an incoming challenge.",
        response: `{ "success": true }`,
      },
    ],
  },
  {
    id: "prizes",
    icon: Trophy,
    title: "Prizes",
    description: "Check prize eligibility and claim rewards.",
    endpoints: [
      {
        method: "GET", path: "/prizes/check", auth: true,
        description: "Check if the user is eligible to claim a prize.",
        response: `{ "eligible": true, "testsCompleted": 50, "thresholdRequired": 50 }`,
      },
      {
        method: "GET", path: "/prizes/history", auth: true,
        description: "Get a list of previously claimed prizes.",
        response: `[{ "id": "...", "prizeType": "gift-card", "claimedAt": "..." }]`,
      },
      {
        method: "POST", path: "/prizes/claim", auth: true,
        description: "Submit a prize claim request.",
        body: {
          phone: "string (required): Jamaican mobile number (876XXXXXXX)",
          network: "string (required): digicel | flow",
        },
        response: `{ "success": true, "message": "Prize claimed! Delivery within 24 hours." }`,
      },
    ],
  },
  {
    id: "messaging",
    icon: MessageSquare,
    title: "Messaging",
    description: "In-app conversations between students.",
    endpoints: [
      {
        method: "GET", path: "/conversations", auth: true,
        description: "Get all the user's conversations.",
        response: `[{ "id": "...", "participant": { "firstName": "..." }, "lastMessage": "...", "unreadCount": 2 }]`,
      },
      {
        method: "POST", path: "/conversations", auth: true,
        description: "Start a new conversation with another student.",
        body: { recipientId: "string (required): user ID of the recipient" },
        response: `{ "conversationId": "...", "participant": { ... } }`,
      },
      {
        method: "GET", path: "/conversations/:conversationId/messages", auth: true,
        description: "Get messages in a specific conversation.",
        response: `[{ "id": "...", "senderId": "...", "content": "...", "sentAt": "..." }]`,
      },
      {
        method: "POST", path: "/conversations/:conversationId/messages", auth: true,
        description: "Send a message in a conversation.",
        body: { content: "string (required)" },
        response: `{ "id": "...", "content": "...", "sentAt": "..." }`,
      },
      {
        method: "GET", path: "/messages/unread-count", auth: true,
        description: "Get the total count of unread messages.",
        response: `{ "count": 3 }`,
      },
    ],
  },
  {
    id: "ai",
    icon: Brain,
    title: "AI Explanations",
    description: "Get personalised AI-generated explanations for any question.",
    endpoints: [
      {
        method: "POST", path: "/questions/:questionId/explain", auth: true,
        description: "Request an AI-generated explanation tailored to the student's performance context.",
        response: `{\n  "explanation": "Great question! To solve this...",\n  "questionText": "What is 3/4 of 48?"\n}`,
        note: "Explanations are generated by GPT-4 and personalised to the student's grade and topic history.",
      },
    ],
  },
  {
    id: "subscription",
    icon: RefreshCw,
    title: "Subscription",
    description: "Check the user's active subscription status.",
    endpoints: [
      {
        method: "GET", path: "/subscription/status", auth: true,
        description: "Get the current subscription status and expiry details.",
        response: `{\n  "status": "active",\n  "plan": "monthly",\n  "expiresAt": "2025-03-01T00:00:00Z",\n  "grade": 5\n}`,
      },
    ],
  },
];

const errorCodes = [
  { code: "NO_AUTH_TOKEN", status: 401, description: "Authorization header is missing" },
  { code: "INVALID_TOKEN", status: 401, description: "Token is invalid or malformed" },
  { code: "USER_NOT_FOUND", status: 404, description: "User does not exist" },
  { code: "EMAIL_EXISTS", status: 409, description: "Email is already registered" },
  { code: "INVALID_CREDENTIALS", status: 401, description: "Incorrect email or password" },
  { code: "SUBSCRIPTION_REQUIRED", status: 403, description: "Active subscription is required" },
  { code: "SUBSCRIPTION_EXPIRED", status: 403, description: "Subscription has expired: renew to continue" },
  { code: "MISSING_PARAMS", status: 400, description: "Required parameters are missing" },
  { code: "SUBJECT_NOT_FOUND", status: 404, description: "Subject not found for this grade" },
  { code: "TOPIC_NOT_FOUND", status: 404, description: "Topic not found for this subject" },
  { code: "QUESTION_NOT_FOUND", status: 404, description: "Question does not exist" },
];

export default function ApiDocs() {
  const [activeSection, setActiveSection] = useState("auth");

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-gradient-to-r from-primary/5 to-chart-2/5">
        <div className="max-w-6xl mx-auto px-4 py-10 md:py-16">
          <Badge className="mb-3 bg-primary/10 text-primary border-primary/20">v1.0.0</Badge>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">EduFocal Mobile API</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mb-6">
            REST API for building iOS and Android apps powered by the EduFocal learning platform. JWT-authenticated, all responses in JSON.
          </p>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 text-sm bg-background border rounded-lg px-3 py-2">
              <span className="text-muted-foreground">Base URL</span>
              <code className="text-primary font-mono font-semibold">https://edufocalacademy.com/api/mobile/v1</code>
            </div>
            <div className="flex items-center gap-2 text-sm bg-background border rounded-lg px-3 py-2">
              <span className="text-muted-foreground">JSON Schema</span>
              <a href="/api/mobile/v1/docs.json" target="_blank" rel="noreferrer" className="text-primary font-mono hover:underline">/api/mobile/v1/docs.json</a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 flex gap-8">
        {/* Sidebar nav */}
        <aside className="hidden md:flex flex-col gap-1 w-52 shrink-0 sticky top-8 self-start">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-2">Sections</p>
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md transition-colors ${
                activeSection === s.id
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              }`}
            >
              <s.icon className="w-4 h-4 shrink-0" />
              {s.title}
            </a>
          ))}
          <Separator className="my-2" />
          <a
            href="#errors"
            onClick={() => setActiveSection("errors")}
            className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md transition-colors ${
              activeSection === "errors"
                ? "bg-primary/10 text-primary font-medium"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
          >
            <ShieldCheck className="w-4 h-4 shrink-0" />
            Error Codes
          </a>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 space-y-10">
          {/* Auth info box */}
          <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
            <CardContent className="pt-5">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                <div className="space-y-2 text-sm">
                  <p className="font-semibold text-blue-800 dark:text-blue-200">JWT Bearer Authentication</p>
                  <p className="text-blue-700 dark:text-blue-300">
                    Protected endpoints require an <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">Authorization</code> header:
                  </p>
                  <CodeBlock code={`Authorization: Bearer <accessToken>`} id="auth-header-example" />
                  <p className="text-blue-700 dark:text-blue-300">
                    Access tokens <strong>do not expire</strong>. Refresh tokens last <strong>30 days</strong>. Use <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">/auth/refresh</code> to rotate them before they lapse.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Endpoint sections */}
          {sections.map((section) => (
            <section key={section.id} id={section.id}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <section.icon className="w-5 h-5 text-primary" />
                    {section.title}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  {section.endpoints.map((ep) => (
                    <EndpointCard key={`${ep.method}-${ep.path}`} ep={ep} />
                  ))}
                </CardContent>
              </Card>
            </section>
          ))}

          {/* Error codes */}
          <section id="errors">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  Error Codes
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  All error responses use this structure: <code className="bg-muted px-1 rounded text-xs">{"{ \"error\": \"message\", \"code\": \"ERROR_CODE\" }"}</code>
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="text-left px-4 py-2.5 font-semibold text-xs uppercase tracking-wide text-muted-foreground">Code</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-xs uppercase tracking-wide text-muted-foreground">HTTP</th>
                        <th className="text-left px-4 py-2.5 font-semibold text-xs uppercase tracking-wide text-muted-foreground">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {errorCodes.map((e, i) => (
                        <tr key={e.code} className={i % 2 === 0 ? "" : "bg-muted/20"}>
                          <td className="px-4 py-2.5">
                            <code className="text-red-600 dark:text-red-400 text-xs font-mono">{e.code}</code>
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground text-xs">{e.status}</td>
                          <td className="px-4 py-2.5 text-xs">{e.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </section>

          <div className="pb-16 text-center text-xs text-muted-foreground">
            EduFocal API v1.0.0. For integration support, contact the EduFocal team.
          </div>
        </main>
      </div>
    </div>
  );
}
